import { config } from "@/config";
import { Firestore } from "@/firestore";
import AuthProvider from "@/providers/auth.provider";
import { appActions } from "@/redux/actions/app.actions";
import { appSelector } from "@/redux/selectors/app.selector";
import { setAppStateAction } from "@/redux/slices/app.slice";
import { useDispatch, useSelector } from "@/redux/store";
import { initializeApp } from "firebase/app";
import type { User } from "firebase/auth";
import { getAuth, signInWithEmailLink, signOut } from "firebase/auth";
import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

export const useAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector(appSelector.user);
  const token = useSelector(appSelector.token);
  const redirectTo = useSelector(appSelector.redirectTo);

  const lastTokenRef = useRef<string | undefined>(token);
  const auth = getAuth();

  const safeNavigate = useCallback(
    (path: string) => {
      navigate(path.startsWith("http") ? path : `/${path.replace(/^\//, "")}`);
    },
    [navigate],
  );

  const onRequestEmail = useCallback(
    async (email: string): Promise<{ success: boolean }> => {
      if (!email) return { success: false };
      dispatch(appActions.setAppState({ loading: true }));

      try {
        const { success } = await AuthProvider.login(email);
        if (success) {
          const emailParam = encodeURIComponent(email);
          navigate(`/invitation-sent?email=${emailParam}`);
        }
        return { success };
      } catch (error) {
        console.error(error);
        return { success: false };
      } finally {
        dispatch(appActions.setAppState({ loading: false }));
      }
    },
    [dispatch, navigate],
  );

  const onAuthStateChange = useCallback(
    async (firebaseUser: User | null) => {
      if (!firebaseUser) {
        dispatch(appActions.setUser(undefined));
        return;
      }

      try {
        const { token: firebaseToken } = await firebaseUser.getIdTokenResult(false);

        lastTokenRef.current = firebaseToken;
        dispatch(setAppStateAction({ token: firebaseToken }));

        dispatch(
          appActions.getUserDetails(firebaseUser, firebaseToken, () => {
            try {
              const instance = initializeApp(config.firebase);
              Firestore.setAppInstance(instance);

              dispatch(setAppStateAction({ loading: false, redirectTo: undefined }));
              if (redirectTo) safeNavigate(redirectTo);
            } catch (err) {
              console.error("Error initializing Firestore:", err);
            }
          }),
        );
      } catch (err) {
        console.error("Error in auth state change:", err);
        navigate("/");
      }
    },
    [dispatch, navigate, redirectTo, safeNavigate],
  );

  const onSignIn = useCallback(
    async (email: string) => {
      if (!email) return;

      dispatch(setAppStateAction({ loading: true }));

      try {
        console.log("[onSignIn] calling signInWithEmailLink");
        const { user: firebaseUser } = await signInWithEmailLink(auth, email, location.href);
        console.log("[onSignIn] signInWithEmailLink succeeded");
        const firebaseToken = await firebaseUser.getIdToken(false);

        await dispatch(
          appActions.getUserDetails(firebaseUser, firebaseToken, () => {
            navigate("/dashboard");
          }),
        );
      } catch (err) {
        const code = (err as { code?: string }).code;
        console.error("[onSignIn] error:", err);
        dispatch(setAppStateAction({ loading: false }));

        if (code === "auth/invalid-action-code") {
          navigate("/token-expired");
        } else if (code) {
          // Firebase auth error (expired link, disabled user, etc.) — send back to login
          navigate("/");
        } else {
          // getUserDetails failed after successful Firebase auth — prompt to retry
          navigate("/?authError=1");
        }
      }
    },
    [auth, dispatch, navigate],
  );

  const onLogout = useCallback(async () => {
    try {
      await AuthProvider.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      signOut(auth);
      localStorage.removeItem("lassoToken");
      dispatch(appActions.logout());
    }
  }, [auth, dispatch]);

  const onUpdateUserDetails = useCallback(
    async (data: {
      firstName: string;
      lastName: string;
      email: string;
      role: string;
      organizationId: string;
      lassoUserId: string;
    }): Promise<{ success: boolean; error?: string }> => {
      try {
        const response = await AuthProvider.updateUserDetails(data);

        if (response.success && user) {
          dispatch(
            setAppStateAction({
              user: {
                ...user,
                firstName: data.firstName,
                lastName: data.lastName,
              },
            }),
          );
        }

        return { success: response.success, error: response.error };
      } catch (error) {
        console.error("Update user details error:", error);
        return { success: false, error: "Failed to update user details" };
      }
    },
    [dispatch, user],
  );

  return {
    onSignIn,
    onLogout,
    onRequestEmail,
    onAuthStateChange,
    onUpdateUserDetails,
    isAuthenticated: !!user,
    user,
  };
};
